import * as THREE from 'three';
import { TransactionShip, ShipClass, TransactionData } from './TransactionShip';

export interface ShipClassification {
  class: ShipClass;
  properties: ShipProperties;
}

export interface ShipProperties {
  scale: number;
  speed: number;
  color: number;
  trailLength: number;
  glowIntensity: number;
}

export class ShipFactory {
  private static shipGeometry: THREE.BufferGeometry | null = null;
  private static shipMaterial: THREE.MeshStandardMaterial | null = null;

  public static initialize(): void {
    // Create shared ship geometry - a simple pointed shape
    const shape = new THREE.Shape();
    shape.moveTo(0, 0.5);
    shape.lineTo(-0.3, -0.5);
    shape.lineTo(0, -0.3);
    shape.lineTo(0.3, -0.5);
    shape.lineTo(0, 0.5);

    const extrudeSettings = {
      depth: 0.2,
      bevelEnabled: true,
      bevelSegments: 2,
      steps: 2,
      bevelSize: 0.05,
      bevelThickness: 0.05
    };

    this.shipGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    this.shipGeometry.center();
    this.shipGeometry.rotateX(-Math.PI / 2);
    
    // Create shared base material
    this.shipMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      metalness: 0.8,
      roughness: 0.2,
      emissive: 0xffffff,
      emissiveIntensity: 0.5
    });
  }

  public static createShip(transactionId: string): TransactionShip {
    if (!this.shipGeometry || !this.shipMaterial) {
      this.initialize();
    }

    const material = this.shipMaterial!.clone();
    const mesh = new THREE.Mesh(this.shipGeometry!, material);
    
    return new TransactionShip(transactionId, mesh);
  }

  public static classifyTransaction(transactionData: TransactionData): ShipClassification {
    const shipClass = this.classifyByFeeRate(transactionData.feeRate);
    const properties = this.getShipProperties(shipClass);
    
    return { class: shipClass, properties };
  }

  private static classifyByFeeRate(feeRate: number): ShipClass {
    if (feeRate < 5) return 'micro';
    if (feeRate < 20) return 'standard';
    if (feeRate < 100) return 'priority';
    return 'whale';
  }

  private static getShipProperties(shipClass: ShipClass): ShipProperties {
    const properties: Record<ShipClass, ShipProperties> = {
      micro: {
        scale: 0.5,
        speed: 0.5,
        color: 0x4488ff,
        trailLength: 10,
        glowIntensity: 0.5
      },
      standard: {
        scale: 1.0,
        speed: 1.0,
        color: 0x44ff88,
        trailLength: 20,
        glowIntensity: 1.0
      },
      priority: {
        scale: 1.5,
        speed: 2.0,
        color: 0xffaa44,
        trailLength: 30,
        glowIntensity: 1.5
      },
      whale: {
        scale: 3.0,
        speed: 3.0,
        color: 0xff4444,
        trailLength: 50,
        glowIntensity: 2.0
      }
    };
    
    return properties[shipClass];
  }

  public static dispose(): void {
    if (this.shipGeometry) {
      this.shipGeometry.dispose();
      this.shipGeometry = null;
    }
    
    if (this.shipMaterial) {
      this.shipMaterial.dispose();
      this.shipMaterial = null;
    }
  }
}